/**
 * Pure, store-free formatting core. `formatters.ts` re-exports everything here, so existing imports
 * keep working; code that must not depend on the auth store (the server PDF renderer bundle, the
 * print adapters) imports this module directly.
 */
import { DISPLAY_FRACTION_DIGITS } from './constants/localizationDefaults';
import type { LocalizationSettings } from '../services/localizationApi';

// Language → Intl locale tag. Extend when new SupportedLanguages are added.
const LANGUAGE_LOCALE: Record<string, string> = {
  tr: 'tr-TR',
  en: 'en-US',
};

export function languageToLocale(language: string): string {
  return LANGUAGE_LOCALE[language] ?? 'en-US';
}

// Building an Intl formatter costs far more than formatting with one, and list pages format several
// values per row on every render. The key space is bounded (locale x tenant settings x the handful
// of option shapes callers pass), so the caches need no eviction.
const numberFormatCache = new Map<string, Intl.NumberFormat>();
const dateTimeFormatCache = new Map<string, Intl.DateTimeFormat>();

function cachedNumberFormat(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let fmt = numberFormatCache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(locale, options);
    numberFormatCache.set(key, fmt);
  }
  return fmt;
}

function cachedDateTimeFormat(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let fmt = dateTimeFormatCache.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(locale, options);
    dateTimeFormatCache.set(key, fmt);
  }
  return fmt;
}

/**
 * Format a date using tenant DateFormat template + optional timezone.
 * Tokens: dd, MM, yyyy. Uses Intl.DateTimeFormat parts so settings.timezone
 * (when set) overrides the browser timezone.
 */
export function formatDate(date: Date | string, settings: LocalizationSettings): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '';
  const parts = cachedDateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: settings.timezone || undefined,
  }).formatToParts(d);
  const dd   = parts.find((p) => p.type === 'day')?.value   ?? '';
  const mm   = parts.find((p) => p.type === 'month')?.value ?? '';
  const yyyy = parts.find((p) => p.type === 'year')?.value  ?? '';
  return settings.dateFormat
    .replace('dd',   dd)
    .replace('MM',   mm)
    .replace('yyyy', yyyy);
}

/**
 * Post-process an Intl-formatted number string to apply explicit
 * DecimalSeparator / ThousandsSeparator overrides from tenant settings.
 *
 * Strategy: Intl always uses a single char for each role. We locate the
 * decimal separator via `formatToParts` (type === 'decimal') and the group
 * separator (type === 'group'), then do a two-pass replacement using a
 * placeholder to avoid double-substitution when the override chars collide
 * with the Intl defaults.
 */
/**
 * Locale → Intl group/decimal separator chars. Invariant per locale, so resolve
 * once via `formatToParts` and cache: `applyExplicitSeparators` runs on every
 * formatNumber/formatCurrency call and building a fresh `Intl.NumberFormat` each
 * time is needless work on large list renders.
 */
const intlSeparatorCache = new Map<string, { decimal: string; thousands: string }>();

function intlSeparators(intlLocale: string): { decimal: string; thousands: string } {
  let cached = intlSeparatorCache.get(intlLocale);
  if (!cached) {
    const parts = new Intl.NumberFormat(intlLocale).formatToParts(1234567.89);
    cached = {
      decimal:   parts.find((p) => p.type === 'decimal')?.value ?? '.',
      thousands: parts.find((p) => p.type === 'group')?.value   ?? ',',
    };
    intlSeparatorCache.set(intlLocale, cached);
  }
  return cached;
}

function applyExplicitSeparators(
  formatted: string,
  intlLocale: string,
  overrideDecimal?: string | null,
  overrideThousands?: string | null,
): string {
  if (!overrideDecimal && !overrideThousands) return formatted;

  const { decimal: intlDecimal, thousands: intlThousands } = intlSeparators(intlLocale);

  let result = formatted;
  const DECIMAL_PLACEHOLDER   = '\u{E001}';
  const THOUSANDS_PLACEHOLDER = '\u{E002}';

  if (overrideDecimal) {
    result = result.split(intlDecimal).join(DECIMAL_PLACEHOLDER);
  }
  if (overrideThousands) {
    result = result.split(intlThousands).join(THOUSANDS_PLACEHOLDER);
  }
  if (overrideDecimal) {
    result = result.split(DECIMAL_PLACEHOLDER).join(overrideDecimal);
  }
  if (overrideThousands) {
    result = result.split(THOUSANDS_PLACEHOLDER).join(overrideThousands);
  }
  return result;
}

/**
 * tenant'ın `displayFractionDigits` ayarını
 * `[DISPLAY_FRACTION_DIGITS.min, DISPLAY_FRACTION_DIGITS.max]` range'inde clamp eder.
 * Storage decimal places ISO 4217 sabit; bu sadece presentation katmanı.
 */
function clampDisplayFractionDigits(value: number | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DISPLAY_FRACTION_DIGITS.default;
  return Math.min(
    DISPLAY_FRACTION_DIGITS.max,
    Math.max(DISPLAY_FRACTION_DIGITS.min, Math.trunc(value)),
  );
}

/**
 * Format a currency value using the record's own currency code + tenant locale/separators.
 * `currency` is required — callers must always pass the record's stored currency explicitly
 * so the display reflects the actual stored value, not the tenant's home-currency setting.
 * When the tenant has explicit DecimalSeparator/ThousandsSeparator overrides,
 * those are applied after Intl formatting via applyExplicitSeparators.
 *
 * `displayFractionDigits` ayarı (range [2, 4], default 2) Intl'ın min/max
 * fraction digits parametrelerine yansıtılır — bu yalnızca presentation; storage decimal
 * places ISO 4217 sabit kalır.
 */
export function formatCurrency(
  value: number | null | undefined,
  currency: string,
  settings: LocalizationSettings,
): string {
  const n = value ?? 0;
  const locale = languageToLocale(settings.language);
  const digits = clampDisplayFractionDigits(settings.displayFractionDigits);
  const formatted = cachedNumberFormat(locale, {
    style: 'currency',
    currency: currency || 'TRY',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n);
  return applyExplicitSeparators(
    formatted,
    locale,
    settings.decimalSeparator,
    settings.thousandsSeparator,
  );
}

/**
 * Format a plain number (no currency symbol).
 *
 * caller `options` vermezse tenant `displayFractionDigits` (default 2)
 * Intl'a min/max fraction olarak verilir. Caller kendi `minimumFractionDigits` /
 * `maximumFractionDigits` veriyorsa bunlar override eder — ondalık-bağımsız
 * formatlamayı bozmamak için.
 */
export function formatNumber(
  value: number | null | undefined,
  settings: LocalizationSettings,
  options?: Intl.NumberFormatOptions,
): string {
  const n = value ?? 0;
  const locale = languageToLocale(settings.language);
  const digits = clampDisplayFractionDigits(settings.displayFractionDigits);
  const mergedOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    ...(options ?? {}),
  };
  const formatted = cachedNumberFormat(locale, mergedOptions).format(n);
  return applyExplicitSeparators(
    formatted,
    locale,
    settings.decimalSeparator,
    settings.thousandsSeparator,
  );
}

/**
 * Format an integer count (no decimals): thousands separator + locale-aware,
 * but the tenant's displayFractionDigits is ignored. For category/product/brand
 * counts etc. — values that are never fractional.
 */
export function formatInteger(
  value: number | null | undefined,
  settings: LocalizationSettings,
): string {
  return formatNumber(value, settings, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

/** Format date + time using language locale + tenant timezone. */
export function formatDateTime(
  date: Date | string,
  settings: LocalizationSettings,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '';
  return cachedDateTimeFormat(languageToLocale(settings.language), {
    ...options,
    timeZone: settings.timezone || undefined,
  }).format(d);
}

/**
 * Tenant date format plus a short time, e.g. `18/09/2026 23:12`. The date part is the same string
 * `formatDate` gives list rows that show no time, so a date-only screen and a date-and-time screen
 * read the same.
 */
export function formatDateWithTime(date: Date | string, settings: LocalizationSettings): string {
  const day = formatDate(date, settings);
  if (!day) return '';
  const time = formatDateTime(date, settings, { timeStyle: 'short' });
  return time ? `${day} ${time}` : day;
}

/**
 * "Bugün" — tenant timezone'unda `YYYY-MM-DD` döner.
 *
 * `new Date().toISOString().slice(0, 10)` paterni
 * browser local timezone'unda "şu an"ı alır ve UTC tarihine çevirir → gece yarısı
 * civarında tenant timezone'undan bir gün önce/sonra düşebilir. Örnek: kullanıcı
 * TR'de (UTC+3), tenant Tokyo (UTC+9). TR 21:00 = Tokyo 03:00 ertesi gün, ama
 * `new Date().toISOString()` UTC 18:00 = ÖNCEKİ gün döner — form date default'u
 * "dün" gelir. Bu helper tenant timezone'una göre doğru günü verir.
 *
 * `<input type="date">` value formatı `YYYY-MM-DD` olduğu için bu string direkt
 * input'a verilebilir.
 */
export function todayInTenantTimezone(settings: LocalizationSettings): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: settings.timezone || undefined,
    year:  'numeric',
    month: '2-digit',
    day:   '2-digit',
  }).format(new Date());
}

export function buildFormatters(settings: LocalizationSettings) {
  return {
    formatCurrency: (v: number | null | undefined, currency: string) => formatCurrency(v, currency, settings),
    formatNumber: (v: number | null | undefined, opts?: Intl.NumberFormatOptions) => formatNumber(v, settings, opts),
    formatInteger: (v: number | null | undefined) => formatInteger(v, settings),
    formatDate: (v: Date | string) => formatDate(v, settings),
    formatDateTime: (v: Date | string, opts?: Intl.DateTimeFormatOptions) => formatDateTime(v, settings, opts),
    formatDateWithTime: (v: Date | string) => formatDateWithTime(v, settings),
    /** Tenant timezone'unda bugünün `YYYY-MM-DD` string'i — `<input type="date">` için. */
    todayInTenantTz: () => todayInTenantTimezone(settings),
    settings,
  };
}
